import { Pencil1Icon, TrashIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  IMasterTranslation,
  IMasterTranslationKey,
} from "../../../@types/admin/AdminMasterTranslations";
import { TableSorted } from "../../../components/TableSorted";
import { useMasterTranslationMutations } from "../../../hooks/mutations/admin/masterTranslations";
import {
  masterTranslationKeysKey,
  useMasterLanguages,
  useMasterTranslationKeys,
  useMasterTranslationsByKey,
  useMasterUsedLocations,
} from "../../../hooks/queries/admin/masterTranslations";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  AdminSelectField,
  BooleanCell,
  ErrorMessage,
  FilterButton,
  FilterGroup,
  FilterLabel,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  RowAction,
  RowActionGroup,
  SearchInput,
  SectionActions,
  SectionCard,
  SectionHeader,
  SectionTitle,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { AddMasterKeyModal } from "./AddMasterKeyModal";
import { SelectionPrompt, TranslationKey } from "./AdminTranslations.styles";
import { ConfirmDeleteMasterModal } from "./ConfirmDeleteMasterModal";
import { RenameMasterKeyModal } from "./RenameMasterKeyModal";
import { UpsertMasterTranslationModal } from "./UpsertMasterTranslationModal";

const SEARCH_FIELDS: (keyof IMasterTranslationKey)[] = ["key", "value"];

interface ISelectedKey {
  key: string;
  usedLocation: string;
}

/**
 * Master Translations editor (EN-5178), migrated from the Retool
 * page_Translations app: CRUD on the master translations store — the single
 * source of truth every environment syncs its Global translations from.
 *
 * Available in every environment whose API has the master DB connection
 * string; elsewhere the API answers 503 (not configured) and the page says so
 * instead of erroring.
 *
 * Editing here changes NO environment: values reach an environment when
 * someone pulls them in on that environment's Translations sync page.
 */
export function AdminMasterTranslations() {
  document.title = "Master translations - Admin";

  const { openModal, closeModal } = useModal();

  const { keys, isLoading, isError, isNotConfigured } =
    useMasterTranslationKeys();
  const { languages } = useMasterLanguages();
  const { usedLocations } = useMasterUsedLocations();

  const [selected, setSelected] = useState<ISelectedKey | null>(null);
  const [locationFilter, setLocationFilter] = useState("");

  const { translations, isLoading: isLoadingDetail } =
    useMasterTranslationsByKey(selected?.key);

  const mutations = useMasterTranslationMutations(selected?.key);

  const locationNames = useMemo(
    () => usedLocations.map((l) => l.usedLocation),
    [usedLocations]
  );

  const filteredKeys = useMemo(
    () =>
      locationFilter
        ? keys.filter((k) => k.usedLocation === locationFilter)
        : keys,
    [keys, locationFilter]
  );

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: filteredKeys, searchFields: SEARCH_FIELDS });

  const existingCodes = useMemo(
    () =>
      selected
        ? translations
            .filter((t) => t.usedLocation === selected.usedLocation)
            .map((t) => t.languageCode)
        : [],
    [translations, selected]
  );

  const handleAddKey = () => {
    openModal(
      <AddMasterKeyModal
        usedLocations={locationNames}
        isSaving={mutations.isUpserting}
        onAdd={async (key, value, usedLocation) => {
          await mutations.upsertTranslation({
            key,
            languageCode: "en-GB",
            usedLocation,
            value,
            archived: false,
          });
        }}
        onClose={closeModal}
      />,
      { width: "520px", showCloseButton: true }
    );
  };

  const handleAddTranslation = () => {
    if (!selected) return;
    openModal(
      <UpsertMasterTranslationModal
        translationKey={selected.key}
        usedLocation={selected.usedLocation}
        languages={languages}
        existingCodes={existingCodes}
        isSaving={mutations.isUpserting}
        onUpsert={async (request) => {
          await mutations.upsertTranslation(request);
        }}
        onClose={closeModal}
      />,
      { width: "520px", showCloseButton: true }
    );
  };

  const handleEditTranslation = (translation: IMasterTranslation) => {
    openModal(
      <UpsertMasterTranslationModal
        translationKey={translation.key}
        usedLocation={translation.usedLocation}
        translation={translation}
        languages={languages}
        existingCodes={[]}
        isSaving={mutations.isUpserting}
        onUpsert={async (request) => {
          await mutations.upsertTranslation(request);
        }}
        onClose={closeModal}
      />,
      { width: "520px", showCloseButton: true }
    );
  };

  const handleEditKey = () => {
    if (!selected) return;
    openModal(
      <RenameMasterKeyModal
        translationKey={selected.key}
        currentUsedLocation={selected.usedLocation}
        usedLocations={locationNames}
        isSaving={mutations.isRenaming}
        onRename={async (newKey, newUsedLocation) => {
          await mutations.renameKey({
            key: selected.key,
            newKey,
            newUsedLocation,
          });
          setSelected({ key: newKey, usedLocation: newUsedLocation });
        }}
        onClose={closeModal}
      />,
      { width: "520px", showCloseButton: true }
    );
  };

  const handleDeleteKey = () => {
    if (!selected) return;
    openModal(
      <ConfirmDeleteMasterModal
        title="Delete key?"
        body={
          <>
            Deletes <strong>{selected.key}</strong> and every one of its
            translations from the master store.
          </>
        }
        confirmLabel="Delete key"
        successMessage={`Key ${selected.key} successfully deleted.`}
        onConfirm={async () => {
          await mutations.deleteKey(selected.key);
          setSelected(null);
        }}
        onClose={closeModal}
      />,
      { width: "480px", showCloseButton: true }
    );
  };

  const handleDeleteTranslation = (translation: IMasterTranslation) => {
    openModal(
      <ConfirmDeleteMasterModal
        title="Delete translation?"
        body={
          <>
            Deletes the <strong>{translation.languageCode}</strong> translation
            of <strong>{translation.key}</strong> from the master store.
          </>
        }
        confirmLabel="Delete translation"
        successMessage="Translation successfully deleted."
        onConfirm={async () => {
          await mutations.deleteTranslation({
            key: translation.key,
            languageCode: translation.languageCode,
            usedLocation: translation.usedLocation,
          });
        }}
        onClose={closeModal}
      />,
      { width: "480px", showCloseButton: true }
    );
  };

  const keyColumns: ColumnDef<IMasterTranslationKey, string>[] = useMemo(
    () => [
      {
        accessorKey: "key",
        header: "Key",
        cell: ({ row }) => (
          <TranslationKey title={row.original.key}>
            {row.original.key}
          </TranslationKey>
        ),
        enableSorting: true,
      },
      {
        id: "value",
        header: "Value (en-GB)",
        accessorFn: (k) => k.value ?? "",
        enableSorting: true,
      },
      {
        id: "languages",
        header: "Languages",
        accessorFn: (k) => k.languageCodes.join(", "),
        enableSorting: false,
      },
      {
        accessorKey: "usedLocation",
        header: "Used location",
        enableSorting: true,
      },
    ],
    []
  );

  const detailColumns: ColumnDef<IMasterTranslation, string>[] = useMemo(
    () => [
      {
        accessorKey: "languageCode",
        header: "Language",
        enableSorting: true,
      },
      {
        accessorKey: "value",
        header: "Value",
        enableSorting: false,
      },
      {
        accessorKey: "usedLocation",
        header: "Used location",
        enableSorting: true,
      },
      {
        id: "archived",
        header: "Archived",
        accessorFn: (t) => (t.archived ? "yes" : "no"),
        cell: ({ row }) => (
          <BooleanCell value={row.original.archived}>
            {row.original.archived ? "Yes" : "No"}
          </BooleanCell>
        ),
        enableSorting: true,
      },
      {
        id: "lastUpdate",
        header: "Last update",
        // A moment in time, so it renders in the viewer's zone (see the
        // "UTC calendar day vs. local timestamp" note in retool-migration.md).
        accessorFn: (t) => getLongFormattedDate(t.lastUpdate),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <RowActionGroup>
            <RowAction
              label="Edit translation"
              icon={<Pencil1Icon />}
              onClick={() => handleEditTranslation(row.original)}
            />
            <RowAction
              label="Delete translation"
              icon={<TrashIcon />}
              danger
              onClick={() => handleDeleteTranslation(row.original)}
            />
          </RowActionGroup>
        ),
        enableSorting: false,
      },
    ],
    // (react-hooks/exhaustive-deps is not configured here; the omitted deps
    // are the modal openers, which are stable for the column cells.)
    [languages, mutations.isUpserting]
  );

  if (isNotConfigured) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>Master translations</PageTitle>
        </PageHeader>
        <ErrorMessage>
          The master translations database is not configured in this
          environment. The editor lives where the master store is hosted — use
          that environment&apos;s portal to manage translation keys.
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Master translations</PageTitle>
          <PageInfoTooltip title="Master translations">
            Every translation key in the master store — the single source of
            truth all environments sync from. Editing here changes no
            environment: values reach one when someone pulls them in on that
            environment&apos;s Translations sync page. Select a key to see and
            edit its translations.
          </PageInfoTooltip>
        </PageTitleGroup>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by key or value..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <FilterGroup>
          <FilterLabel htmlFor="master-location-filter">
            Used location
          </FilterLabel>
          <AdminSelectField
            id="master-location-filter"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
          >
            <option value="">All locations</option>
            {usedLocations.map((location) => (
              <option key={location.usedLocation} value={location.usedLocation}>
                {location.usedLocation} ({location.count})
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FiltersActions>
          <FilterButton variant="primary" onClick={handleAddKey}>
            Add key
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the master translations. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[masterTranslationKeysKey]}>
          <TableSorted
            columns={keyColumns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No keys found"}
            onRowClick={(row: IMasterTranslationKey) =>
              setSelected({ key: row.key, usedLocation: row.usedLocation })
            }
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "master-translations",
            columns: keyColumns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>

      {!selected ? (
        <SelectionPrompt>
          Select a key to see and edit its translations.
        </SelectionPrompt>
      ) : (
        <SectionCard>
          <SectionHeader>
            <SectionTitle css={{ overflowWrap: "anywhere" }}>
              {selected.key}
            </SectionTitle>
            <SectionActions>
              <FilterButton variant="secondary" onClick={handleEditKey}>
                Edit key
              </FilterButton>
              <FilterButton variant="secondary" onClick={handleDeleteKey}>
                Delete key
              </FilterButton>
              <FilterButton variant="primary" onClick={handleAddTranslation}>
                Add translation
              </FilterButton>
            </SectionActions>
          </SectionHeader>
          <TableWrapper refreshKeys={[["admin-master-translations-by-key"]]}>
            <TableSorted
              columns={detailColumns}
              data={translations}
              emptyStateText={
                isLoadingDetail ? <TableLoadingState /> : "No translations"
              }
            />
          </TableWrapper>
        </SectionCard>
      )}
    </PageContainer>
  );
}
